import {
  DOC_NUMBER,
  CONTACT_MEDIA,
  NEW_CONTACT_MEDIA,
  STEPS,
  STEPS_TEXTS,
  STEP_TITLE,
  STEPS_TAGGING_TEXT,
} from '@/constants/account';
import { legalTerms } from '@/constants/legalTerms';
import { useDispatch } from 'react-redux';
import Text, {
  TEXT_COLOR,
  TEXT_SIZE,
  TEXT_DISPLAY,
} from '@/bit/components/components.text';
import Button, {
  BUTTON_TYPE,
  BUTTON_WIDTH,
  BUTTON_TYPE_HTML,
  BUTTON_OPERATION,
} from '@/bit/components/components.button';
import CustomLink from '@/bit/components/components.custom-link';
import FormFake from '@/components/fakes/formPagesFake/formAccount';
import FormTemplate, { THEMES, TITLE_SIZE } from '@/components/formTemplate';
import InputController from '@/components/inputController/index';
import { actions as modalActions } from '@/bit/reducers/reducers.modals-reducer';
import dynamic from 'next/dynamic';

function AccountForm({ step, control, loading, contactMedia, formError }) {
  const dispatch = useDispatch();
  const handleLegalModal = () => {
    const LegalModal = dynamic(() =>
      import('@/bit/components/components.legal-modal')
    );
    dispatch(
      modalActions.addModal({
        id: 'legal-modal',
        Element2Show: LegalModal,
        modalProps: {
          contentPosition: 'right--top',
          opacityLayoutColor: 'blur',
          inSceneEffect: 'fadeIn',
          contentInsideContainer: false,
          outSceneEffect: 'fadeOut',
          closeByClickingOutside: true,
          scrollAllowed: false,
          createAnchor: false,
        },
        elementProps: {
          title: {
            text: 'Verificaci&oacute;n avanzada: t&eacute;rminos y condiciones',
            size: TEXT_SIZE.TITLE5,
            color: TEXT_COLOR.GREY6,
          },
          description: { text: legalTerms },
          taggingInfo: {},
        },
      })
    );
  };
  return (
    <>
      {step && (
        <FormTemplate
          titleText={STEP_TITLE[step]}
          theme={THEMES.FULL}
          titleSize={TITLE_SIZE.T1}
        >
          {STEPS_TEXTS[step] && Array.isArray(STEPS_TEXTS[step]) && (
            <>
              {STEPS_TEXTS[step].map((text, id) => {
                const key = `${STEPS_TEXTS[step]}${id}`;
                return (
                  <div className="step__text" key={key}>
                    <Text
                      text={text}
                      size={TEXT_SIZE.CAPTION2}
                      color={TEXT_COLOR.GREY5}
                      display={TEXT_DISPLAY.BLOCK}
                      cyData={`${STEPS_TEXTS[step]}__text`}
                    />
                  </div>
                );
              })}
            </>
          )}
          {(step === STEPS.NEW_ACCOUNT || step === STEPS.RECOVERY_ACCOUNT) && (
            <InputController
              className="form__input"
              inputName={DOC_NUMBER.NAME}
              inputId={DOC_NUMBER.ID}
              control={control}
              rules={{
                required: DOC_NUMBER.RULES.REQUIRED,
                validate: (fieldValue) =>
                  DOC_NUMBER.RULES.VALIDATE.DOC_NUMBER_FORMAT.checkFunc(
                    fieldValue
                  ) || DOC_NUMBER.RULES.VALIDATE.DOC_NUMBER_FORMAT.message,
              }}
              label={DOC_NUMBER.LABEL}
            />
          )}
          {step === STEPS.CONTACT_MEDIA && (
            <InputController
              className="form__input"
              inputName={CONTACT_MEDIA.NAME}
              inputId={CONTACT_MEDIA.ID}
              control={control}
              rules={{
                required: CONTACT_MEDIA.RULES.REQUIRED,
                validate: {
                  format: (fieldValue) =>
                    CONTACT_MEDIA.RULES.VALIDATE.CONTACT_MEDIA_FORMAT.checkFunc(
                      fieldValue
                    ) ||
                    CONTACT_MEDIA.RULES.VALIDATE.CONTACT_MEDIA_FORMAT.message,
                  prevContactMedia: (fieldValue) =>
                    CONTACT_MEDIA.RULES.VALIDATE.PREV_CONTACT_MEDIA.checkFunc(
                      fieldValue,
                      formError && contactMedia
                    ) || formError,
                },
              }}
              label={CONTACT_MEDIA.LABEL}
            />
          )}
          {step === STEPS.NEW_CONTACT_MEDIA && (
            <InputController
              className="form__input"
              inputName={NEW_CONTACT_MEDIA.NAME}
              inputId={NEW_CONTACT_MEDIA.ID}
              control={control}
              rules={{
                required: NEW_CONTACT_MEDIA.RULES.REQUIRED,
                validate: {
                  format: (fieldValue) =>
                    NEW_CONTACT_MEDIA.RULES.VALIDATE.CONTACT_MEDIA_FORMAT.checkFunc(
                      fieldValue
                    ) ||
                    NEW_CONTACT_MEDIA.RULES.VALIDATE.CONTACT_MEDIA_FORMAT
                      .message,
                  prevContactMedia: (fieldValue) =>
                    CONTACT_MEDIA.RULES.VALIDATE.PREV_CONTACT_MEDIA.checkFunc(
                      fieldValue,
                      formError && contactMedia
                    ) || formError,
                },
              }}
              label={NEW_CONTACT_MEDIA.LABEL}
            />
          )}
          <Button
            label="Continuar"
            type={BUTTON_TYPE.PRIMARY}
            width={BUTTON_WIDTH.FULL}
            buttonTypeHtml={BUTTON_TYPE_HTML.SUBMIT}
            operation={loading ? BUTTON_OPERATION.LOADING : BUTTON_OPERATION.NO}
            disabled={loading}
            taggingInfo={{
              componentName: 'button',
              componentVariation: 'all',
              componentContent: STEPS_TAGGING_TEXT[step] || 'Continuar',
              componentProduct: '',
            }}
            cyData="Continuar"
          />
          {step === STEPS.NEW_CONTACT_MEDIA && (
            <CustomLink
              className="legal-term-link"
              title="Consultar los términos y condiciones"
              ariaLabel="Consultar los términos y condiciones"
              onClick={handleLegalModal}
              taggingInfo={{
                componentName: 'trade-in-link',
                componentContent: 'Consultar los términos y condiciones',
                componentVariation: 'all',
                componentPerso: '',
                componentProduct: '',
              }}
            >
              <Text
                text="Consultar los términos y condiciones"
                color={TEXT_COLOR.BLUEA}
                size={TEXT_SIZE.CAPTION2}
                display={TEXT_DISPLAY.INLINE}
              />
            </CustomLink>
          )}
        </FormTemplate>
      )}
      {!step && <FormFake withHeader />}
      <style jsx>
        {`
          .step__text {
            padding: 8px 0 14px 0px;
          }
          :global(.form__input) {
            padding: 8px 0 24px 0px;
            width: 100%;
          }

          :global(.form__input:first-child) {
            padding: 16px 0 24px 0px;
            width: 100%;
          }
          :global(.legal-term-link) {
            padding: 24px 0 24px 0px;
            width: 100%;
            display: flex;
            justify-content: center;
          }
          :global(.legals-obd) {
            font-family: Regular;
            font-size: 1rem;
            color: var(--color-gp--grey5);
            :global(h1) {
              font-family: Regular;
              font-size: 1rem;
              color: var(--color-gp--grey6);
              font-weight: bold;
            }
            :global(h2, h3) {
              padding-top: 8px;
              font-family: Regular;
              font-size: 1rem;
              color: var(--color-gp--grey6);
              font-weight: bold;
            }
            :global(div) {
              padding-top: 10px;
            }
            :global(p) {
              padding-top: 8px;
              padding-bottom: 8px;
              font-family: Regular;
              font-size: 1rem;
              color: var(--color-gp--grey5);
            }
            :global(ol) {
              padding-top: 4px;
              padding-bottom: 4px;
              font-family: Regular;
              font-size: 1rem;
              color: var(--color-gp--grey5);
            }
            :global(ul) {
              padding-top: 10px;
              padding-bottom: 10px;
              font-family: Regular;
              font-size: 1rem;
              color: var(--color-gp--grey5);
            }
            :global(link) {
              padding-top: 4px;
              padding-bottom: 4px;
              font-family: Regular;
              font-size: 1rem;
              color: var(--color-gp--grey5);
            }
          }
        `}
      </style>
    </>
  );
}
export default AccountForm;
